import UserGuide from '@/components/help/UserGuide';
import { useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { PERMISSION_CATALOG, roleLabel } from '@klothiq/domain';
import { accessActionClass } from './user-access-styles';

export interface PendingRoleChange {
  role: 'admin' | 'staff';
  removed: string[];
}

export function RoleChangeConfirmation({
  change,
  onCancel,
  onConfirm,
  onReturnFocus,
}: {
  change: PendingRoleChange | null;
  onCancel: () => void;
  onConfirm: () => void;
  onReturnFocus: () => void;
}) {
  const cancelButton = useRef<HTMLButtonElement>(null);
  return (
    <Dialog.Root
      open={!!change}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" data-field-guide-scope />
        <Dialog.Content
          className="obsidian-panel fixed left-1/2 top-1/2 z-[60] flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            cancelButton.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            onReturnFocus();
          }}
        >
          <div className="min-h-0 space-y-4 overflow-y-auto p-5">
            <div className="flex items-start justify-between gap-3 pr-8">
              <Dialog.Title className="text-lg font-semibold">
                Change role to {roleLabel(change?.role)}?
              </Dialog.Title>
              <UserGuide compact />
            </div>
            <Dialog.Description className="text-sm leading-relaxed text-muted-foreground">
              These privileges are unavailable for the selected role and will be removed from this
              draft. Saved access changes only when you submit the account form.
            </Dialog.Description>
            <ul className="space-y-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-sm">
              {change?.removed.map((key) => (
                <li key={key} className="break-words">
                  {PERMISSION_CATALOG.find((p) => p.key === key)?.label ?? key}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid shrink-0 grid-cols-2 gap-3 border-t border-border p-4">
            <button
              ref={cancelButton}
              type="button"
              className={accessActionClass}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`${accessActionClass} border-primary bg-primary text-primary-foreground hover:bg-primary/90`}
              onClick={onConfirm}
            >
              Change role
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
